import React, { useEffect, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Check, X } from 'lucide-react';
import { getCampaignRuns } from '../../services/api';
import { relative } from './scheduleText';
import { ATTENTION_MSG } from './runPolling';

const TRIGGER = { manual: 'Manual', schedule: 'Scheduled', rollback: 'Rollback' };
const RESULT = {
  pass: { label: 'Passed', cls: 'bg-[#06C755]/10 text-[#06C755] border-[#06C755]/30' },
  fail: { label: 'Failed', cls: 'bg-red-500/10 text-red-400 border-red-500/30' },
  rolled_back: { label: 'Rolled back', cls: 'bg-[#27272A] text-[#A1A1AA] border-[#3F3F46]' },
};
const absFmt = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });
const trunc = (s, n = 120) => { const t = typeof s === 'string' ? s : JSON.stringify(s ?? ''); return t.length > n ? `${t.slice(0, n)}…` : t; };

function RunRow({ run, menuNames }) {
  const [open, setOpen] = useState(false);
  const started = run.startedAt ? new Date(run.startedAt) : null;
  const res = RESULT[run.result] || { label: run.phase === 'done' ? 'Unknown' : 'In progress', cls: 'bg-amber-500/10 text-amber-400 border-amber-500/30' };
  const steps = run.steps || [];
  return (
    <li className="border border-[#27272A] rounded-md" data-testid="run-item">
      <button type="button" className="w-full flex items-center gap-3 px-3 py-2 text-left" onClick={() => setOpen(!open)} aria-expanded={open}>
        {open ? <ChevronDown className="w-4 h-4 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 flex-shrink-0" />}
        <span className="text-sm flex-1 min-w-0">
          {started ? `${relative(started)} · ${absFmt.format(started)}` : '—'}
          <span className="text-[#A1A1AA]"> · {TRIGGER[run.trigger] || run.trigger}</span>
        </span>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${res.cls}`} data-testid="run-result">{res.label}</span>
      </button>
      {(run.error || run.needsAttention || (run.problems || []).length > 0) && (
        <div className="px-3 pb-2 space-y-2">
          {run.needsAttention && (
            <div className="flex items-start gap-2 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2" data-testid="run-attention">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {ATTENTION_MSG}
            </div>
          )}
          {run.error && <p className="text-sm text-red-300" data-testid="run-error">{run.error}</p>}
          {(run.problems || []).length > 0 && (
            <ul className="space-y-1">
              {run.problems.map((p, i) => (
                <li key={`${p.code}-${i}`} className="text-sm text-red-300" data-testid="run-problem">{p.menuName ? `${p.menuName}: ` : ''}{p.message}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {open && (
        <div className="px-3 pb-3 overflow-x-auto" data-testid="run-steps">
          {steps.length === 0 ? <p className="text-sm text-[#A1A1AA]">No steps recorded.</p> : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[#A1A1AA]"><th className="py-1 pr-3">Step</th><th className="pr-3">Menu</th><th className="pr-3">OK</th><th className="pr-3">LINE</th><th>Response</th></tr>
              </thead>
              <tbody>
                {steps.map((s, i) => (
                  <tr key={i} className="border-t border-[#27272A] align-top">
                    <td className="py-1 pr-3 whitespace-nowrap">{s.step}</td>
                    <td className="pr-3">{menuNames[s.menuId] || s.menuId || '—'}</td>
                    <td className="pr-3">{s.ok ? <Check className="w-3.5 h-3.5 text-[#06C755]" aria-label="ok" /> : <X className="w-3.5 h-3.5 text-red-400" aria-label="failed" />}</td>
                    <td className="pr-3">{s.lineStatus ?? '—'}</td>
                    <td className="text-[#A1A1AA] break-all">{s.lineBody ? trunc(s.lineBody) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </li>
  );
}

export default function RunHistory({ campaignId, members, refreshKey }) {
  const [runs, setRuns] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getCampaignRuns(campaignId)
      .then((d) => { if (!cancelled) { setRuns(d.runs || []); setFailed(false); } })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [campaignId, refreshKey]);

  const menuNames = Object.fromEntries((members || []).map((m) => [m.id, m.name]));

  return (
    <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-4" data-testid="run-history">
      <h2 className="font-semibold mb-3">Run history</h2>
      {failed && !runs && <p className="text-sm text-red-300">Couldn't load run history.</p>}
      {runs && runs.length === 0 && <p className="text-sm text-[#A1A1AA]">No runs yet.</p>}
      {runs && runs.length > 0 && (
        <ul className="space-y-2">{runs.map((r) => <RunRow key={r.id} run={r} menuNames={menuNames} />)}</ul>
      )}
    </div>
  );
}
